import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';

import { Div, Icon, Tag, Text } from '@pinpoint/design-kit';

import CONSTANTS from 'src/store/constants';
import { Rbac, Ubac } from 'src/containers';
import { ROUTES } from 'src/config';
import { getSegmentTagStyles } from '../../functions';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

const ONPOINT_WORKFLOW = process.env.REACT_APP_ONPOINT_WORKFLOW || 'https://stage-workflow.vpn.selfgrowth.guide';
const makeUrl = (dt) => `${ONPOINT_WORKFLOW}/graph?dag_id=det_onboarding&execution_date=${encodeURIComponent(dt)}`;

export function CellActions({
  value: {
    cufId,
    workflows,
    latestState,
    datasetName,
    datasetTag,
    onboardingType,
    onboardingPayload,
    onboardingId,
    isReOnboardable,
  },
  history,
  handleApproveClick,
  handleConfirmClick,
  // handleDownloadClick,
}) {
  const onConfirmClick = () => handleConfirmClick(cufId);
  const onApproveClick = () => handleApproveClick(cufId);
  return (
    <>
      <Ubac users={CONSTANTS.ENG_TEAM}>
        {workflows.map(({ id, dag_execution_date: dagExecutionDate }) => (
          <a key={id} href={makeUrl(dagExecutionDate)} target="_blank" rel="noreferrer">
            <Icon
              name="Link"
              color="tabSelected"
              hoverTransform="scale(1.25)"
              cursor="pointer"
              m={{ r: '0.3125rem' }}
              size=".75rem"
            />
          </a>
        ))}
      </Ubac>
      {latestState?.state === CONSTANTS.ONBOARDING_STATUS.API.DET_WAITING_FOR_CONFIRMATION && (
        <Rbac
          roles={[
            CONSTANTS.ROLE.ADMIN,
            CONSTANTS.ROLE.SUPER_ADMIN,
            CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN,
            CONSTANTS.ROLE.CLIENT_SERVICES,
          ]}
          fallback={
            <Icon
              color="tabSelected"
              name="Checked"
              hoverTransform="scale(1.25)"
              size=".75rem"
              cursor="not-allowed"
              data-tip="Only admins can confirm onboarding"
              m={{ x: '0.3125rem' }}
            />
          }
        >
          <Icon
            color="tabSelected"
            name="Checked"
            hoverTransform="scale(1.25)"
            size=".75rem"
            cursor="pointer"
            m={{ x: '0.3125rem' }}
            data-tip="Confirm and proceed with onboarding"
            onClick={onConfirmClick}
          />
        </Rbac>
      )}
      {latestState?.state === CONSTANTS.ONBOARDING_STATUS.API.WAITING_FOR_APPROVAL && (
        <Rbac
          roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES]}
          fallback={
            <Icon
              color="tabSelected"
              name="Checked"
              hoverTransform="scale(1.25)"
              size=".75rem"
              cursor="not-allowed"
              data-tip="Only pinpoint admins can approve onboarding"
              m={{ x: '0.3125rem' }}
            />
          }
        >
          <Icon
            color="tabSelected"
            name="Checked"
            hoverTransform="scale(1.25)"
            size=".75rem"
            cursor="pointer"
            m={{ x: '0.3125rem' }}
            data-test="apv-act-onb"
            data-tip="Approve and proceed with onboarding"
            onClick={onApproveClick}
          />
        </Rbac>
      )}
      {CONSTANTS.REONBOARDABLE_STATES.includes(latestState.state) && isReOnboardable && (
        <Rbac
          roles={[
            CONSTANTS.ROLE.ADMIN,
            CONSTANTS.ROLE.SUPER_ADMIN,
            CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN,
            CONSTANTS.ROLE.CLIENT_SERVICES,
          ]}
          fallback={
            <Icon
              color="tabSelected"
              name="Download"
              hoverTransform="rotate(180deg) scale(1.25)"
              size=".75rem"
              cursor="not-allowed"
              data-tip="Only admins can confirm onboarding"
              m={{ x: '0.3125rem' }}
            />
          }
        >
          <Icon
            color="tabSelected"
            name="Download"
            transform={isReOnboardable ? 'rotate(180deg)' : undefined}
            size=".75rem"
            hoverTransform={isReOnboardable ? 'rotate(180deg) scale(1.25)' : 'scale(1.25)'}
            cursor={isReOnboardable ? 'pointer' : 'not-allowed'}
            m={{ x: '0.3125rem' }}
            data-tip={isReOnboardable ? 'Upload a new file' : "This onboarding can't be updated"}
            onClick={() => {
              if (isReOnboardable) {
                history.push(ROUTES.NEW_ONBOARDING, {
                  datasetName,
                  datasetTag,
                  onboardingType,
                  onboardingPayload,
                  onboardingId,
                });
              }
            }}
          />
        </Rbac>
      )}
    </>
  );
}

CellActions.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  value: PropTypes.shape({
    cufId: PropTypes.number.isRequired,
    workflows: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        dag_execution_dates: PropTypes.arrayOf(PropTypes.string).isRequired,
      })
    ).isRequired,
    latestState: PropTypes.shape({
      state: PropTypes.string.isRequired,
    }).isRequired,
    datasetName: PropTypes.string.isRequired,
    datasetTag: PropTypes.string.isRequired,
    onboardingType: PropTypes.string.isRequired,
    onboardingPayload: PropTypes.string.isRequired,
    onboardingId: PropTypes.number.isRequired,
    isReOnboardable: PropTypes.bool.isRequired,
  }).isRequired,
  handleApproveClick: PropTypes.func.isRequired,
  handleConfirmClick: PropTypes.func.isRequired,
  // handleDownloadClick: PropTypes.func.isRequired,
};

export function CellDate({ value }) {
  return (
    <Text
      data-tip={dayjs(value).format('HH:mm [GMT]Z (zzz) — ddd, MMM D, YYYY')}
      textSize="small"
      style={{
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {dayjs(value).format('MMM DD, YYYY')}
    </Text>
  );
}

CellDate.propTypes = {
  value: PropTypes.instanceOf(Date).isRequired,
};

export function CellText({ value }) {
  return (
    <Text
      data-tip={value}
      textSize="small"
      style={{
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Text>
  );
}

CellText.propTypes = {
  value: PropTypes.string.isRequired,
};

export function CellStatus({ value }) {
  switch (value) {
    case CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_RAW_CLIENT_UPLOAD_FAILURE:
    case CONSTANTS.ONBOARDING_STATUS.API.ACCEPTED_RAW_CLIENT_UPLOAD_FAILURE:
    case CONSTANTS.ONBOARDING_STATUS.API.PROCESSED_BY_DET_FAILURE:
      return <Div rounded="sm" bg="vividRed" h=".9rem" w=".9rem" data-tip="Error" />;
    case CONSTANTS.ONBOARDING_STATUS.API.PROFILE_MATCHED:
      return <Div rounded="sm" bg="rioGrande" h=".9rem" w=".9rem" data-tip="Completed" />;
    case CONSTANTS.ONBOARDING_STATUS.API.DET_WAITING_FOR_CONFIRMATION:
    case CONSTANTS.ONBOARDING_STATUS.API.WAITING_FOR_APPROVAL:
      return <Div rounded="sm" bg="marigold" h=".9rem" w=".9rem" data-tip="Pending approval" />;
    case CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_RAW_CLIENT_UPLOAD:
    case CONSTANTS.ONBOARDING_STATUS.API.ACCEPTED_RAW_CLIENT_UPLOAD:
    case CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_CLIENT_UPLOAD:
    case CONSTANTS.ONBOARDING_STATUS.API.ACCEPT_CLIENT_UPLOAD:
    case CONSTANTS.ONBOARDING_STATUS.API.PROCESSED_BY_DET:
    case CONSTANTS.ONBOARDING_STATUS.API.APPROVED_BY_ADMIN:
    case CONSTANTS.ONBOARDING_STATUS.API.EXTRACTED_FOR_PUSH:
    case CONSTANTS.ONBOARDING_STATUS.API.PACKAGED_FOR_PROVIDER:
    case CONSTANTS.ONBOARDING_STATUS.API.PUSHED_TO_PROVIDER:
    case CONSTANTS.ONBOARDING_STATUS.API.READY_FOR_PROVIDER:
    case CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_FROM_PROVIDER:
    default:
      return <Div rounded="sm" bg="blueLight" h=".9rem" w=".9rem" data-tip="In Progress" />;
  }
}

CellStatus.defaultProps = {
  value: null,
};

CellStatus.propTypes = {
  value: PropTypes.string,
};

export function CellTag({ value }) {
  if (!CONSTANTS.SEGMENT.TAGS.includes(value)) return <Tag bg="transparent">&ndash; &ndash; &ndash;</Tag>;
  const { color, name, viewBox, tip } = getSegmentTagStyles(value);
  return (
    <Tag
      p={{ x: '.375rem', y: '.188rem' }}
      // @ts-ignore
      h="1.125rem"
      textColor={color}
      borderColor={color}
      border="1px solid"
      data-tip={tip}
    >
      <Icon name={name} size=".75rem" viewBox={viewBox} color={color} m={{ r: '.25rem' }} />
      <Text textSize="small">{CONSTANTS.SEGMENT.TAG[value]}</Text>
    </Tag>
  );
}

CellTag.propTypes = {
  value: PropTypes.string.isRequired,
};
